/**
 * Version selection modal: language-specific version dropdowns and deployment options.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React, { useMemo } from 'react';
import { Box, Button, Checkbox, Container, Modal, Select, SpaceBetween } from '@amzn/awsui-components-core';
import { DeploymentOptions, LambdaFunction, SelectedVersions } from '../types';
import { RUNTIME_CONFIGS, ResourceTypeConfig, getLanguageFromRuntime } from '../config';

interface VersionSelectionModalProps {
  visible: boolean;
  onDismiss: () => void;
  currentConfig: ResourceTypeConfig;
  selectedLambdasCount: number;
  selectedLambdas: LambdaFunction[];
  selectedLanguageCounts: Record<string, number>;
  selectedVersions: SelectedVersions;
  setSelectedVersions: React.Dispatch<React.SetStateAction<SelectedVersions>>;
  deploymentOptions: DeploymentOptions;
  setDeploymentOptions: React.Dispatch<React.SetStateAction<DeploymentOptions>>;
  getSelectedPipelines: () => string[];
  onConfirm: () => void;
}

export const VersionSelectionModal: React.FC<VersionSelectionModalProps> = ({
  visible,
  onDismiss,
  currentConfig,
  selectedLambdasCount,
  selectedLambdas,
  selectedLanguageCounts,
  selectedVersions,
  setSelectedVersions,
  deploymentOptions,
  setDeploymentOptions,
  getSelectedPipelines,
  onConfirm
}) => {
  const selectedPipelineNames = getSelectedPipelines();
  const isManualOnlySelection = selectedPipelineNames.every((name) => name === 'Manually created' || name === 'Manual Deployed Stacks');

  // Compute current runtime version counts per language (e.g. { python: { "python3.9": 40, "python3.12": 35 } })
  const currentVersionsByLanguage = useMemo(() => {
    const result: Record<string, Record<string, number>> = {};
    selectedLambdas.forEach((lambda) => {
      if (!lambda.runtime) return;
      const config = getLanguageFromRuntime(lambda.runtime);
      if (config) {
        if (!result[config.language]) result[config.language] = {};
        result[config.language][lambda.runtime] = (result[config.language][lambda.runtime] || 0) + 1;
      }
    });
    return result;
  }, [selectedLambdas]);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Select Target Versions - ${currentConfig.displayName}`}
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm}>
              Start Update
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box>Select the target version for each category detected in your selection.</Box>

        {Object.entries(selectedLanguageCounts).map(([lang, count]) => {
          const config = RUNTIME_CONFIGS.find((c) => c.language === lang);
          if (!config) return null;

          return (
            <Container key={lang}>
              <SpaceBetween size="s">
                <Box fontWeight="bold">
                  {config.icon} {config.displayName} ({count} resources)
                </Box>
                {currentVersionsByLanguage[lang] && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    Current:{' '}
                    {Object.entries(currentVersionsByLanguage[lang])
                      .sort(([, a], [, b]) => b - a)
                      .map(([runtime, cnt]) => `${runtime} (${cnt})`)
                      .join(', ')}
                  </Box>
                )}
                <Select
                  selectedOption={
                    config.versions.find((v) => v.value === selectedVersions[lang])
                      ? { value: selectedVersions[lang], label: config.versions.find((v) => v.value === selectedVersions[lang])?.label || '' }
                      : null
                  }
                  onChange={({ detail }) => {
                    setSelectedVersions((prev) => ({ ...prev, [lang]: detail.selectedOption?.value || '' }));
                  }}
                  options={config.versions.map((v) => ({ value: v.value, label: v.label }))}
                  placeholder="Select version"
                />
              </SpaceBetween>
            </Container>
          );
        })}

        <Container>
          <SpaceBetween size="s">
            <Box fontWeight="bold">🔧 Deployment Options</Box>
            <Checkbox
              checked={deploymentOptions.deployToAlpha}
              onChange={({ detail }) => setDeploymentOptions((prev) => ({ ...prev, deployToAlpha: detail.checked }))}
              disabled={isManualOnlySelection}
            >
              Deploy to Alpha (Deploy changes to Alpha environment for testing)
              {isManualOnlySelection && (
                <Box color="text-body-secondary" fontSize="body-s">
                  <br />
                  Not available for manually created/deployed resources (direct Lambda update)
                </Box>
              )}
            </Checkbox>
          </SpaceBetween>
        </Container>

        <Box>
          <strong>Selected Resources:</strong> {selectedLambdasCount}
        </Box>
      </SpaceBetween>
    </Modal>
  );
};
